/**
 * The calendar's period (US-032): a month or a week, read from and written to
 * the address as `month=YYYY-MM` or `week=YYYY-MM-DD` (the Monday). Every day
 * here is a local calendar day, built with `new Date(y, m, d)` and stepped with
 * `setDate`, so a clock change inside the period never shifts a day.
 */

export type CalendarMode = 'month' | 'week';

export type CalendarPeriod =
  | { readonly mode: 'month'; readonly year: number; readonly month: number }
  | { readonly mode: 'week'; readonly monday: Date };

export const CALENDAR_MODES: readonly { readonly value: CalendarMode; readonly label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
];

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** A local day as `YYYY-MM-DD`, the way a `dueOn` is written. */
export function dayKey(date: Date): string {
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local midnight of the day `days` after `date`. */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** Local midnight of the Monday on or before `date`. */
export function mondayOf(date: Date): Date {
  return addDays(date, -((date.getDay() + 6) % 7));
}

/** A real `YYYY-MM-DD` as local midnight, or `null`. */
function readDay(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

/** The period of `now` in that mode: this month, or this week. */
export function periodOf(mode: CalendarMode, now: Date): CalendarPeriod {
  return mode === 'month'
    ? { mode, year: now.getFullYear(), month: now.getMonth() + 1 }
    : { mode, monday: mondayOf(now) };
}

/**
 * The period an address asks for: `week` (any day snaps to its Monday), else
 * `month`, else this month. A value that is no real date is ignored.
 */
export function readPeriod(params: URLSearchParams, now: Date): CalendarPeriod {
  const week = readDay(params.get('week') ?? '');
  if (week !== null) return { mode: 'week', monday: mondayOf(week) };
  const month = /^(\d{4})-(\d{2})$/.exec(params.get('month') ?? '');
  if (month !== null && Number(month[2]) >= 1 && Number(month[2]) <= 12) {
    return { mode: 'month', year: Number(month[1]), month: Number(month[2]) };
  }
  return periodOf('month', now);
}

/** What the period writes into the address: one of `month` and `week`. */
export function periodParams(period: CalendarPeriod): { readonly month: string | null; readonly week: string | null } {
  return period.mode === 'month'
    ? { month: `${String(period.year)}-${pad(period.month)}`, week: null }
    : { month: null, week: dayKey(period.monday) };
}

/** The days on show: six weeks from the Monday on or before the 1st, or the seven days of the week. */
export function periodDays(period: CalendarPeriod): Date[] {
  const first = period.mode === 'month' ? mondayOf(new Date(period.year, period.month - 1, 1)) : period.monday;
  return Array.from({ length: period.mode === 'month' ? 42 : 7 }, (_, index) => addDays(first, index));
}

/** The range to read: from the local start of the first day to the local end of the last (exclusive). */
export function periodRange(period: CalendarPeriod): { readonly from: string; readonly to: string } {
  const days = periodDays(period);
  const first = days[0] ?? new Date();
  const last = days[days.length - 1] ?? first;
  return { from: first.toISOString(), to: addDays(last, 1).toISOString() };
}

/** The period before (`-1`) or after (`1`) this one. */
export function stepPeriod(period: CalendarPeriod, delta: 1 | -1): CalendarPeriod {
  if (period.mode === 'week') return { mode: 'week', monday: addDays(period.monday, 7 * delta) };
  const date = new Date(period.year, period.month - 1 + delta, 1);
  return { mode: 'month', year: date.getFullYear(), month: date.getMonth() + 1 };
}

/**
 * Month ↔ Week. A month becomes the week of today when today is in that
 * month, else its first week; a week becomes the month its Monday is in.
 */
export function switchMode(period: CalendarPeriod, mode: CalendarMode, now: Date): CalendarPeriod {
  if (period.mode === mode) return period;
  if (period.mode === 'week') {
    return { mode: 'month', year: period.monday.getFullYear(), month: period.monday.getMonth() + 1 };
  }
  const inMonth = now.getFullYear() === period.year && now.getMonth() + 1 === period.month;
  return { mode: 'week', monday: mondayOf(inMonth ? now : new Date(period.year, period.month - 1, 1)) };
}

/** The ISO 8601 week number of a day: the week its Thursday falls in. */
export function isoWeek(date: Date): number {
  const thursday = addDays(date, 3 - ((date.getDay() + 6) % 7));
  // Counted in UTC days, so a 23- or 25-hour day does not tip the division.
  const day = Date.UTC(thursday.getFullYear(), thursday.getMonth(), thursday.getDate());
  const january1 = Date.UTC(thursday.getFullYear(), 0, 1);
  return Math.floor((day - january1) / 86_400_000 / 7) + 1;
}

/** "October 2026", or "Week 42 · 12–18 Oct 2026" (both ends named when the week spans two months or years). */
export function periodTitle(period: CalendarPeriod): string {
  if (period.mode === 'month') return `${MONTH_NAMES[period.month - 1] ?? ''} ${String(period.year)}`;
  const start = period.monday;
  const end = addDays(start, 6);
  const day = (date: Date): string => `${String(date.getDate())} ${MONTH_SHORT[date.getMonth()] ?? ''}`;
  const span =
    start.getFullYear() !== end.getFullYear()
      ? `${day(start)} ${String(start.getFullYear())}–${day(end)} ${String(end.getFullYear())}`
      : start.getMonth() !== end.getMonth()
        ? `${day(start)}–${day(end)} ${String(end.getFullYear())}`
        : `${String(start.getDate())}–${day(end)} ${String(end.getFullYear())}`;
  return `Week ${String(isoWeek(start))} · ${span}`;
}

/**
 * The same local time of day as `at`, on `day` (`YYYY-MM-DD`): where a post
 * dragged to another day lands (US-033). Built from local parts, so a
 * daylight-saving change between the two days keeps the clock time.
 */
export function onDay(at: Date, day: string): Date | null {
  const date = readDay(day);
  if (date === null) return null;
  date.setHours(at.getHours(), at.getMinutes(), at.getSeconds(), at.getMilliseconds());
  return date;
}
